import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ChatMessage,
  ChatRequest,
  ChatResponse,
  ChatResponseData,
  ChatTableData,
  ChatResponseType
} from '../interfaces/chatbot.interface';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {

  private readonly STORAGE_KEY = 'kosmos_chatbot_history_v1';
  private conversationId: string = this.generateUUID();
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  private isTypingSubject = new BehaviorSubject<boolean>(false);
  public isTyping$: Observable<boolean> = this.isTypingSubject.asObservable();

  private processingStatusSubject = new BehaviorSubject<string>('');
  public processingStatus$: Observable<string> = this.processingStatusSubject.asObservable();
  private streamingMessageId: string | null = null;
  // Debe superar el timeout del backend (120 s) para no cancelar el procesamiento antes que Gemini.
  private readonly STREAM_TIMEOUT_MS = 300000;

  constructor() {
    this.initWelcomeMessage();
  }

  /**
   * Verifica si el usuario autenticado posee los roles / grupos autorizados en su token de login:
   * 'ROLE_CHATBOT_VDI' o 'ROLE_CHATBOT_COM' (uno, otro o ambos).
   */
  public hasChatbotAccess(): boolean {
    const allowedRoles = ['ROLE_CHATBOT_VDI', 'ROLE_CHATBOT_COM'];
    try {
      // 1. Extraer del token JWT (accessToken)
      const token = localStorage.getItem('accessToken');
      if (token && token.includes('.')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const groups: string[] = [];

        if (Array.isArray(payload?.groups)) {
          groups.push(...payload.groups);
        }
        if (Array.isArray(payload?.roles)) {
          groups.push(...payload.roles);
        }
        if (Array.isArray(payload?.authorities)) {
          groups.push(...payload.authorities.map((a: any) => (typeof a === 'string' ? a : a.authority || a.authorityName)));
        }
        if (typeof payload?.groups === 'string') {
          groups.push(payload.groups);
        }

        if (allowedRoles.some((role) => groups.includes(role))) {
          return true;
        }
      }

      // 2. Extraer de datosUsuarioLogin si existen grupos / authorities en el perfil
      const datosStr = localStorage.getItem('datosUsuarioLogin');
      if (datosStr) {
        const datos = JSON.parse(datosStr);
        const userGroups: string[] = [];
        if (Array.isArray(datos?.groups)) userGroups.push(...datos.groups);
        if (Array.isArray(datos?.roles)) userGroups.push(...datos.roles);
        if (Array.isArray(datos?.authorities)) {
          userGroups.push(...datos.authorities.map((a: any) => (typeof a === 'string' ? a : a.authority || a.authorityName)));
        }
        if (allowedRoles.some((role) => userGroups.includes(role))) {
          return true;
        }
      }
    } catch (e) {
      console.warn('[ChatbotService] Error al verificar roles del chatbot:', e);
    }
    return false;
  }

  /**
   * Obtiene exclusivamente el primer nombre de pila del usuario autenticado en Kosmos.
   * Maneja el formato corporativo de Active Directory/SAP: "APELLIDO1 APELLIDO2 NOMBRE1 NOMBRE2"
   * (ejemplo: "MENDOSA SALTOS MAYRA ALEJANDRA" -> "Mayra").
   */
  public getLoggedUserName(): string {
    try {
      const datosStr = localStorage.getItem('datosUsuarioLogin');
      if (datosStr) {
        const datos = JSON.parse(datosStr);
        if (datos) {
          const fn = (datos.firstName || datos.nombre || '').trim();
          const ln = (datos.lastName || datos.apellido || '').trim();

          // Si existen campos separados reales
          if (fn && ln) {
            // Si fn contiene la cadena completa con apellidos y nombres
            if (fn.split(/\s+/).length > 2) {
              return this.extractFirstNameFromFullName(fn);
            }
            // Si fn son solo los nombres de pila (ej: "MAYRA ALEJANDRA")
            return this.formatName(fn.split(/\s+/)[0]);
          }

          // Si viene en fn pero sin ln
          if (fn) {
            return this.extractFirstNameFromFullName(fn);
          }

          // Si viene en login2, dscaUsuario, nombreCompleto o usuario
          const rawFull = datos.login2 || datos.dscaUsuario || datos.nombreCompleto || datos.usuario || '';
          if (rawFull && typeof rawFull === 'string') {
            const extracted = this.extractFirstNameFromFullName(rawFull);
            if (extracted) return extracted;
          }
        }
      }

      // Token JWT
      const token = localStorage.getItem('accessToken');
      if (token && token.includes('.')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const rawTokenName = payload?.given_name || payload?.firstName || payload?.nombre || payload?.name || payload?.login2 || '';
        if (rawTokenName && typeof rawTokenName === 'string') {
          const extracted = this.extractFirstNameFromFullName(rawTokenName);
          if (extracted) return extracted;
        }
      }

      // Fallback a userName
      const userName = localStorage.getItem('userName') || localStorage.getItem('usuario') || '';
      if (userName && !userName.includes('@')) {
        return this.formatName(userName.split('.')[0]);
      }
    } catch (e) {
      console.warn('[ChatbotService] No se pudo obtener el nombre de usuario:', e);
    }
    return '';
  }

  /**
   * Extrae el primer nombre a partir del formato "APELLIDO1 APELLIDO2 NOMBRE1 [NOMBRE2]".
   * Ejemplos:
   * - "MENDOSA SALTOS MAYRA ALEJANDRA" -> "Mayra"
   * - "MENDOZA LOOR MARIO"             -> "Mario"
   * - "MENDOSA MAYRA"                  -> "Mayra"
   * - "MAYRA"                          -> "Mayra"
   */
  private extractFirstNameFromFullName(fullName: string): string {
    if (!fullName || typeof fullName !== 'string') return '';
    const clean = fullName.trim();
    if (!clean) return '';

    const words = clean.split(/\s+/);

    // 4 o más palabras: "MENDOSA SALTOS MAYRA ALEJANDRA" -> words[2] es "MAYRA"
    if (words.length >= 4) {
      return this.formatName(words[2]);
    }

    // 3 palabras: "MENDOZA SALTOS MAYRA" -> words[2] es "MAYRA"
    if (words.length === 3) {
      return this.formatName(words[2]);
    }

    // 2 palabras: "MENDOSA MAYRA" -> words[1] es "MAYRA"
    if (words.length === 2) {
      return this.formatName(words[1]);
    }

    // 1 palabra: "MAYRA" -> words[0]
    return this.formatName(words[0]);
  }

  /**
   * Da formato Capitalizado (Title Case) a una palabra (ej: MARIO -> Mario).
   */
  private formatName(name: string): string {
    if (!name) return '';
    const clean = name.trim();
    return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
  }

  /**
   * Inicializa el historial del chat con un mensaje de bienvenida personalizado.
   */
  private initWelcomeMessage(): void {
    const userName = this.getLoggedUserName();
    const saludoPersonalizado = userName ? `¡Hola, **${userName}**!` : '¡Hola!';

    const welcomeMessage: ChatMessage = {
      id: 'msg-welcome',
      sender: 'bot',
      text: `${saludoPersonalizado} Soy tu asistente de gestión comercial y ventas. Puedes consultarme sobre tus ventas, rentabilidad, clientes y productos.`,
      type: 'TEXT',
      isWelcome: true,
      suggestedActions: [
        '¿Cuál fue la venta neta del ultimo trimestre?',
        '¿Cuál es la venta neta de la agencia Manta de la ultima semana?',
        'Top 10 clientes con mayores compras del mes',
        'Top 5 productos más vendidos del mes'
      ],
      timestamp: new Date()
    };
    this.messagesSubject.next([welcomeMessage]);
  }

  /**
   * Obtiene la lista actual de mensajes.
   */
  public getMessages(): ChatMessage[] {
    return this.messagesSubject.getValue();
  }

  /**
   * Obtiene el identificador de usuario (username/login) activo en la sesión para enviarlo al backend.
   */
  public getLoggedUsername(): string {
    try {
      // 1. Desde datosUsuarioLogin
      const datosStr = localStorage.getItem('datosUsuarioLogin');
      if (datosStr) {
        const datos = JSON.parse(datosStr);
        if (datos?.login) return String(datos.login).trim();
        if (datos?.usuario) return String(datos.usuario).trim();
        if (datos?.username) return String(datos.username).trim();
      }

      // 2. Desde localStorage directo
      const userName = localStorage.getItem('userName') || localStorage.getItem('usuario') || localStorage.getItem('user');
      if (userName && userName.trim()) {
        return userName.trim();
      }

      // 3. Desde JWT token
      const token = localStorage.getItem('accessToken');
      if (token && token.includes('.')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload?.sub) return String(payload.sub).trim();
        if (payload?.preferred_username) return String(payload.preferred_username).trim();
        if (payload?.username) return String(payload.username).trim();
        if (payload?.login) return String(payload.login).trim();
      }
    } catch (e) {
      console.warn('[ChatbotService] No se pudo obtener el username:', e);
    }
    return '';
  }

  /**
   * Envía un mensaje al backend y procesa la respuesta SSE por POST.
   * Los heartbeats y estados reinician el timeout, pero nunca se muestran como respuesta.
   */
  public sendMessage(rawText: string, currentRoute: string = ''): Observable<ChatResponse> {
    const sanitizedText = this.sanitizeInput(rawText);
    if (!sanitizedText) {
      return of(this.createErrorResponse('El mensaje no puede estar vacío.'));
    }

    const userMessage: ChatMessage = {
      id: this.generateUUID(),
      sender: 'user',
      text: sanitizedText,
      timestamp: new Date()
    };

    this.messagesSubject.next([...this.getMessages(), userMessage]);
    this.isTypingSubject.next(true);
    this.processingStatusSubject.next('Consultando datos comerciales...');

    const username = this.getLoggedUsername();
    const payload: ChatRequest = {
      message: sanitizedText,
      username,
      conversationId: this.conversationId,
      context: {
        currentRoute: currentRoute || '/dashboard',
        timezone: 'America/Guayaquil',
        clientTimestamp: new Date().toISOString(),
        username
      }
    };

    return this.streamChat(this.getApiUrl(), payload).pipe(
      catchError((error) => {
        const detail = error instanceof Error ? error.message : String(error);
        console.error('[ChatbotService] Error al procesar el stream SSE:', detail);
        return of(this.createErrorResponse(this.getStreamErrorMessage(detail)));
      }),
      tap((response: ChatResponse) => this.publishFinalResponse(response))
    );
  }

  private streamChat(url: string, payload: ChatRequest): Observable<ChatResponse> {
    return new Observable<ChatResponse>((observer) => {
      const controller = new AbortController();
      let buffer = '';
      let accumulatedText = '';
      let finalResponse: ChatResponse | null = null;
      let structuredData: ChatResponseData = {};
      let graphData: any;
      let timeoutId: ReturnType<typeof setTimeout>;
      let lastEventType = '';
      let terminalEventReceived = false;
      let settled = false;

      const resetTimeout = (): void => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          settled = true;
          controller.abort();
          observer.error(new Error('Tiempo de espera agotado durante el procesamiento del chatbot.'));
        }, this.STREAM_TIMEOUT_MS);
      };

      const processEvent = (event: any): void => {
        resetTimeout();
        if (!event || !event.type) return;
        lastEventType = event.type;

        switch (event.type) {
          case 'heartbeat':
            return;
          case 'status':
            this.processingStatusSubject.next(event.text || 'Procesando consulta...');
            return;
          case 'token':
            accumulatedText += event.text || '';
            this.updateStreamingMessage(accumulatedText);
            return;
          case 'message':
            terminalEventReceived = true;
            finalResponse = this.toChatResponse(event, accumulatedText);
            const markdownTable = this.extractMarkdownTable(finalResponse.textResponse);
            if (!structuredData.table && markdownTable) {
              structuredData.table = markdownTable.table;
              finalResponse.textResponse = markdownTable.text;
            }
            finalResponse.data = { ...structuredData, ...finalResponse.data };
            if (finalResponse.data.table) {
              finalResponse.type = 'TABLE';
            }
            accumulatedText = finalResponse.textResponse;
            return;
          case 'table':
            terminalEventReceived = true;
            structuredData.table = this.toTableData(event);
            finalResponse = {
              ...this.toChatResponse(event, accumulatedText),
              type: 'TABLE',
              data: structuredData
            };
            accumulatedText = finalResponse.textResponse;
            return;
          case 'graph':
            terminalEventReceived = true;
            graphData = event.data || event.graph || event.payload;
            finalResponse = this.mergeGraphResponse(finalResponse, event, accumulatedText);
            structuredData = { ...structuredData, ...finalResponse.data };
            finalResponse.data = structuredData;
            if (finalResponse.data.table) {
              finalResponse.type = 'TABLE';
            }
            return;
          case 'error':
            throw new Error(event.detail || event.text || 'El servicio de asistente no pudo completar la consulta.');
          default:
            console.warn('[ChatbotService] Evento SSE no reconocido:', event.type);
        }
      };

      const readStream = async (): Promise<void> => {
        try {
          resetTimeout();
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              ...this.getSecureHeaders(),
              Accept: 'text/event-stream'
            },
            body: JSON.stringify(payload),
            signal: controller.signal
          });

          if (!response.ok || !response.body) {
            throw new Error(`HTTP ${response.status}`);
          }

          resetTimeout();
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            if (value && value.length > 0) {
              resetTimeout();
            }

            buffer += decoder.decode(value, { stream: true });
            const events = buffer.split(/\r?\n\r?\n/);
            buffer = events.pop() || '';
            events.forEach((rawEvent) => this.parseSseEvent(rawEvent, processEvent));
          }

          buffer += decoder.decode();
          if (buffer.trim()) {
            this.parseSseEvent(buffer, processEvent);
          }

          if (!terminalEventReceived) {
            throw new Error(`El stream SSE terminó sin evento terminal. Último evento: ${lastEventType || 'ninguno'}.`);
          }

          const responseToPublish = finalResponse || this.toChatResponse({ type: 'message' }, accumulatedText);
          if (!responseToPublish.textResponse && responseToPublish.type !== 'ERROR') {
            throw new Error('El servicio de asistente no devolvió una respuesta válida.');
          }
          settled = true;
          observer.next(responseToPublish);
          observer.complete();
        } catch (error) {
          if (!settled) observer.error(error);
        } finally {
          clearTimeout(timeoutId);
        }
      };

      readStream();
      return () => {
        settled = true;
        clearTimeout(timeoutId);
        controller.abort();
      };
    });
  }

  /**
   * Limpia la conversación y reinicia el estado.
   */
  public clearConversation(): void {
    this.conversationId = this.generateUUID();
    this.initWelcomeMessage();
  }

  /**
   * Sanitización de entrada de texto para prevenir inyección de caracteres no válidos o sobrecarga.
   */
  private sanitizeInput(input: string): string {
    if (!input) return '';
    return input
      .trim()
      .replace(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F]/g, '') // Elimina caracteres de control
      .substring(0, 500); // Límite seguro de caracteres
  }

  private parseSseEvent(rawEvent: string, processEvent: (event: any) => void): void {
    const dataLines = rawEvent
      .split(/\r?\n/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trim());

    if (!dataLines.length) return;

    const payload = dataLines.join('\n');
    if (payload === '[DONE]') return;

    try {
      processEvent(JSON.parse(payload));
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('El servicio de asistente devolvió un evento inválido.');
      }
      throw error;
    }
  }

  private toTableData(event: any): ChatTableData {
    const columns = Array.isArray(event.columns) ? event.columns.map((column: any) => String(column)) : [];
    const rows = Array.isArray(event.rows)
      ? event.rows.filter((row: any) => Array.isArray(row)) as (string | number)[][]
      : [];
    return { columns, rows };
  }

  private extractMarkdownTable(text: string): { table: ChatTableData; text: string } | null {
    const lines = text.split(/\r?\n/);
    const separatorIndex = lines.findIndex((line) =>
      /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line)
    );
    if (separatorIndex < 1) return null;

    const splitRow = (line: string): string[] => {
      const normalized = line.trim().replace(/^\|/, '').replace(/\|$/, '');
      return normalized.split('|').map((cell) => cell.trim().replace(/\*\*/g, ''));
    };

    const columns = splitRow(lines[separatorIndex - 1]);
    const rows: (string | number)[][] = [];
    let endIndex = separatorIndex;
    for (let index = separatorIndex + 1; index < lines.length; index++) {
      if (!lines[index].includes('|') || !lines[index].trim()) break;
      rows.push(splitRow(lines[index]));
      endIndex = index;
    }

    if (!columns.length || !rows.length) return null;
    const remainingText = lines.slice(0, separatorIndex - 1)
      .concat(lines.slice(endIndex + 1))
      .join('\n')
      .trim();
    return { table: { columns, rows }, text: remainingText };
  }

  private toChatResponse(event: any, accumulatedText: string): ChatResponse {
    const response = event.response || event.payload || event;
    const responseType = ['TEXT', 'METRIC_SUMMARY', 'TABLE', 'ERROR'].includes(response.type)
      ? response.type
      : 'TEXT';
    return {
      conversationId: response.conversationId || this.conversationId,
      textResponse: response.textResponse || response.text || accumulatedText || '',
      type: responseType,
      data: response.data,
      suggestedActions: response.suggestedActions
    };
  }

  private mergeGraphResponse(current: ChatResponse | null, event: any, accumulatedText: string): ChatResponse {
    const graphData = event.data || event.graph || event.payload;
    const response = current || this.toChatResponse(event, accumulatedText);
    const graphType = graphData?.table ? 'TABLE' : graphData?.metrics ? 'METRIC_SUMMARY' : 'TEXT';
    return {
      ...response,
      data: graphData || response.data,
      type: response.type === 'ERROR' ? 'ERROR' : graphType
    };
  }

  private updateStreamingMessage(text: string): void {
    if (!this.streamingMessageId) {
      this.streamingMessageId = this.generateUUID();
      const streamingMessage: ChatMessage = {
        id: this.streamingMessageId,
        sender: 'bot',
        text,
        type: 'TEXT',
        timestamp: new Date()
      };
      this.messagesSubject.next([...this.getMessages(), streamingMessage]);
      return;
    }

    const updatedMessages = this.getMessages().map((message) =>
      message.id === this.streamingMessageId ? { ...message, text } : message
    );
    this.messagesSubject.next(updatedMessages);
  }

  private publishFinalResponse(response: ChatResponse): void {
    this.isTypingSubject.next(false);
    this.processingStatusSubject.next('');
    if (response.conversationId) {
      this.conversationId = response.conversationId;
    }

    const botMessage: ChatMessage = {
      id: this.streamingMessageId || this.generateUUID(),
      sender: 'bot',
      text: response.textResponse,
      type: response.type,
      data: response.data,
      suggestedActions: response.suggestedActions,
      timestamp: new Date()
    };
    const messageExists = this.streamingMessageId && this.getMessages().some((message) => message.id === this.streamingMessageId);
    const updatedMessages = messageExists
      ? this.getMessages().map((message) => message.id === this.streamingMessageId ? botMessage : message)
      : [...this.getMessages(), botMessage];
    this.messagesSubject.next(updatedMessages);
    this.streamingMessageId = null;
  }

  /**
   * Construye URL base del servicio de chatbot expuesto en el backend Quarkus.
   * Ruta: https://apidev.lafabril.com.ec/qrk/app/ms/adam-api/api/adam
   */
  private getApiUrl(): string {
    const protocol = environment.protocol || 'https';
    const domain = environment.domain || 'apidev.lafabril.com.ec';
    const port = environment.port ? `:${environment.port}` : '';
    const gateway = environment.gateway ? `/${environment.gateway}` : '/qrk/app/ms';
    return `${protocol}://${domain}${port}${gateway}/adam-api/api/adam`;
  }

  /**
   * Genera headers con token de autenticación de forma segura para fetch.
   */
  private getSecureHeaders(): Record<string, string> {
    let token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
    if (token && !token.startsWith('Bearer ') && !token.startsWith('bearer ')) {
      token = `Bearer ${token}`;
    }

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {})
    };
  }

  /**
   * Generador de UUID para IDs de mensaje y sesión.
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private getStreamErrorMessage(detail: string): string {
    if (detail.includes('sin evento terminal')) {
      return 'La conexión con el agente se cerró antes de recibir el resultado. Intente nuevamente.';
    }
    if (detail.includes('Tiempo de espera agotado')) {
      return 'La consulta está tardando más de lo esperado. Intente nuevamente en unos momentos.';
    }
    return 'No fue posible completar la consulta del asistente. Intente más tarde.';
  }

  /**
   * Respuesta de error controlada.
   */
  private createErrorResponse(message: string): ChatResponse {
    this.isTypingSubject.next(false);
    this.processingStatusSubject.next('');
    return {
      conversationId: this.conversationId,
      textResponse: message,
      type: 'ERROR'
    };
  }

}
